import React, { forwardRef } from 'react';
import { Icon } from '../icons';
import type { IconName } from '../icons/types';
import styles from './Input.module.css';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  leftIcon?: IconName;
  rightIcon?: IconName;
  onRightIconClick?: () => void;
  fullWidth?: boolean;
  className?: string;
}

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  fullWidth?: boolean;
  className?: string;
  rows?: number;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      hint,
      size = 'md',
      variant = 'default',
      leftIcon,
      rightIcon,
      onRightIconClick,
      fullWidth = false,
      className,
      id,
      disabled,
      ...props
    },
    ref,
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const wrapperClasses = [
      styles.inputWrapper,
      styles[size],
      styles[variant],
      fullWidth && styles.fullWidth,
      error && styles.hasError,
      success && styles.hasSuccess,
      disabled && styles.disabled,
      leftIcon && styles.hasLeftIcon,
      rightIcon && styles.hasRightIcon,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClasses = [
      styles.input,
      leftIcon && styles.withLeftIcon,
      rightIcon && styles.withRightIcon,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}

        <div className={styles.inputContainer}>
          {leftIcon && <Icon name={leftIcon} className={styles.leftIcon} />}

          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            disabled={disabled}
            {...props}
          />

          {rightIcon && (
            <button
              type="button"
              className={styles.rightIcon}
              onClick={onRightIconClick}
              disabled={disabled}
              tabIndex={onRightIconClick ? 0 : -1}
            >
              <Icon name={rightIcon} />
            </button>
          )}
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <Icon name="alertCircle" className={styles.messageIcon} />
            {error}
          </div>
        )}

        {success && !error && (
          <div className={styles.successMessage}>
            <Icon name="checkCircle" className={styles.messageIcon} />
            {success}
          </div>
        )}

        {hint && !error && !success && (
          <div className={styles.hintMessage}>{hint}</div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      success,
      hint,
      size = 'md',
      variant = 'default',
      fullWidth = false,
      className,
      id,
      disabled,
      rows = 4,
      resize = 'vertical',
      ...props
    },
    ref,
  ) => {
    const textareaId =
      id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    const wrapperClasses = [
      styles.inputWrapper,
      styles[size],
      styles[variant],
      fullWidth && styles.fullWidth,
      error && styles.hasError,
      success && styles.hasSuccess,
      disabled && styles.disabled,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const textareaClasses = [styles.textarea, styles[resize]]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label htmlFor={textareaId} className={styles.label}>
            {label}
          </label>
        )}

        <div className={styles.inputContainer}>
          <textarea
            ref={ref}
            id={textareaId}
            className={textareaClasses}
            disabled={disabled}
            rows={rows}
            {...props}
          />
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <Icon name="alertCircle" className={styles.messageIcon} />
            {error}
          </div>
        )}

        {success && !error && (
          <div className={styles.successMessage}>
            <Icon name="checkCircle" className={styles.messageIcon} />
            {success}
          </div>
        )}

        {hint && !error && !success && (
          <div className={styles.hintMessage}>{hint}</div>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
